import * as React from "react";
import Image from "next/image";
import Link from "next/link";

import { Container } from "./container";
import { CookieSettingsLink } from "./cookie-settings-link";
import { Logo } from "./logo";
import { LinkedInIcon, InstagramIcon, TikTokIcon } from "./social-icons";
import {
  footerColumns,
  footerCopyright,
  footerTagline,
  socialLinks,
  subFooterLinks,
} from "@/lib/content/nav";

const socialIcons = {
  linkedin: LinkedInIcon,
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
} as const;

const linkClass =
  "text-clarus-dark/70 transition-colors hover:text-clarus-dark";

const bodyStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.875rem",
  lineHeight: 1.55,
};

const headingStyle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 800,
  fontSize: "0.9375rem",
};

/** Full-bleed divider — used between the mobile-stacked sections so it
 *  spans edge-to-edge instead of being indented by the Container's
 *  horizontal padding. Matches the 2px page-section dividers. */
function FooterDivider({ className = "" }: { className?: string }) {
  return (
    <hr
      aria-hidden="true"
      className={`border-0 border-t-2 border-clarus-dark ${className}`}
    />
  );
}

/** Black-red-gold German flag, drawn inline so it stays crisp and matches
 *  the project's SVG-icon approach. Decorative — the adjacent
 *  "Made in Germany" text carries the meaning. `preserveAspectRatio="none"`
 *  lets the three equal stripes fill whatever box the wrapper gives it. */
function GermanFlag() {
  return (
    <svg
      viewBox="0 0 5 3"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="block h-full w-full"
    >
      <rect width="5" height="1" y="0" fill="#000000" />
      <rect width="5" height="1" y="1" fill="#DD0000" />
      <rect width="5" height="1" y="2" fill="#FFCE00" />
    </svg>
  );
}

/** Heart accent via Material Symbols (Rounded), the site-wide icon set. Decorative — the adjacent
 *  "with" text carries the meaning of the "Made in Germany with ♥" line. */
function HeartIcon() {
  return (
    <span
      aria-hidden="true"
      translate="no"
      className="ms-icon is-filled text-[18px] text-clarus-dark"
    >
      favorite
    </span>
  );
}

/** Membership + social — shared between mobile and desktop blocks so the
 *  markup only lives in one place. Laid out inline as
 *  "Mitglied bei: [BVDW]  ·  Folge uns: [icons]", separated by a centered
 *  middle dot; wraps gracefully in the narrow desktop column. A
 *  "Made in Germany" badge is centered on desktop (absolutely positioned),
 *  while membership + social stay left-aligned. */
function SocialAndBvdw() {
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-3 lg:relative"
      style={{ ...bodyStyle, fontWeight: 400 }}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-clarus-dark">Mitglied bei:</span>
        <Link
          href="https://www.bvdw.org"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="BVDW – Bundesverband Digitale Wirtschaft e.V. (öffnet neues Fenster)"
          title="Bundesverband Digitale Wirtschaft e.V."
          className="inline-flex items-center rounded-md text-clarus-dark transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2"
        >
          <Image
            src="/icons/bvdw.png"
            alt="BVDW Mitglied — Bundesverband Digitale Wirtschaft e.V."
            width={128}
            height={128}
            className="h-11 w-11"
          />
        </Link>
      </div>

      {/* Centered middle-dot separators between the groups */}
      <span
        aria-hidden="true"
        className="hidden text-[1.25rem] leading-none text-clarus-dark/40 lg:inline-block"
      >
        ·
      </span>

      <div className="flex items-center gap-2.5">
        <span className="text-clarus-dark">Folge uns:</span>
        <div className="flex gap-1">
          {socialLinks.map((s) => {
            const Icon = socialIcons[s.icon];
            return (
              <Link
                key={s.icon}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.label} (öffnet neues Fenster)`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-clarus-dark transition-opacity hover:opacity-75"
              >
                <Icon className="h-6 w-6" aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2 lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
        <span className="text-clarus-dark">Made in Germany</span>
        <span className="inline-flex h-4 w-6 shrink-0 overflow-hidden rounded-[3px] border border-clarus-dark/20">
          <GermanFlag />
        </span>
        <span className="text-clarus-dark">with</span>
        <HeartIcon />
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer
      className="bg-background text-clarus-dark border-t-2 border-clarus-dark"
      aria-label="Footer"
    >
      {/* -- Desktop layout (lg+) — original 12-column grid with logo +
              social + BVDW on the left, link columns on the right. -- */}
      <Container className="hidden py-16 lg:block">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo />
            <p
              className="mt-5 max-w-xs text-clarus-dark/70"
              style={bodyStyle}
            >
              {footerTagline}
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-4">
            {footerColumns.map((column) => (
              <div key={`desktop-${column.title}`}>
                <h2 className="text-clarus-dark" style={headingStyle}>
                  {column.title}
                </h2>
                <ul className="mt-4 space-y-3" style={bodyStyle}>
                  {column.links.map((link) => (
                    <li key={`desktop-${column.title}-${link.label}`}>
                      <Link href={link.href} className={linkClass}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 border-t-[1.5px] border-clarus-dark/15 pt-8">
          {/* Full-width row so membership + verification + social fit on one line */}
          <SocialAndBvdw />
          <div
            className="mt-8 flex flex-col gap-4 text-clarus-dark/70 sm:flex-row sm:items-center sm:justify-between"
            style={bodyStyle}
          >
            <p>{footerCopyright}</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {subFooterLinks.map((link) => (
                <li key={`desktop-${link.label}`}>
                  {link.label === "Cookie-Einstellungen" ? (
                    <CookieSettingsLink label={link.label} className={linkClass} />
                  ) : (
                    <Link href={link.href} className={linkClass}>
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>

      {/* -- Mobile / tablet layout (<lg) — each block stacks vertically
              and is separated by a full-bleed 2px divider that matches the
              page-section dividers above. -- */}
      <div className="lg:hidden">
        <Container className="pt-10">
          <Logo />
          <p
            className="mt-5 max-w-xs text-clarus-dark/70"
            style={bodyStyle}
          >
            {footerTagline}
          </p>
          <div className="h-10" />
        </Container>

        <FooterDivider />
        <Container className="py-8">
          <SocialAndBvdw />
        </Container>

        <FooterDivider />
        <Container className="py-8">
          <div className="grid grid-cols-2 gap-x-8 gap-y-10">
            {footerColumns.map((column) => (
              <div key={`mobile-${column.title}`}>
                <h2 className="text-clarus-dark" style={headingStyle}>
                  {column.title}
                </h2>
                <ul className="mt-4 space-y-3" style={bodyStyle}>
                  {column.links.map((link) => (
                    <li key={`mobile-${column.title}-${link.label}`}>
                      <Link href={link.href} className={linkClass}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Container>

        <FooterDivider />
        <Container className="py-6">
          <ul
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-clarus-dark/70"
            style={bodyStyle}
          >
            {subFooterLinks.map((link) => (
              <li key={`mobile-${link.label}`}>
                {link.label === "Cookie-Einstellungen" ? (
                  <CookieSettingsLink label={link.label} className={linkClass} />
                ) : (
                  <Link href={link.href} className={linkClass}>
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Container>

        <FooterDivider />
        <Container className="py-4">
          <p
            className="text-center text-clarus-dark/70"
            style={bodyStyle}
          >
            {footerCopyright}
          </p>
        </Container>
      </div>
    </footer>
  );
}
